import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  useCountries,
  useDepartments,
  usePositions,
  useHolidays,
  useCreateHoliday,
  useDeleteHoliday,
  useCreateDepartment,
  useDeleteDepartment,
  useCreatePosition,
  useDeletePosition,
  useUsers,
  useUpdateUserRole,
  usePolicies,
  useUpsertPolicy,
  useDeletePolicy,
} from '@/lib/queries';

type Section =
  | 'paises'
  | 'catalogs'
  | 'roles'
  | 'politicas';

const MENU = [
  { key: 'paises' as const, label: 'Países y regiones' },
  { key: 'catalogs' as const, label: 'Catálogos' },
  { key: 'roles' as const, label: 'Roles y permisos' },
  { key: 'politicas' as const, label: 'Políticas de vacaciones' },
];

export function ConfigPage() {
  const [section, setSection] = useState<Section>('paises');

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-font-primary">Configuración</h1>
        <p className="mt-1 text-sm text-font-secondary">
          Administra los parámetros globales del sistema
        </p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar menu */}
        <div className="w-64 flex-shrink-0 rounded-lg border border-border bg-white p-2 shadow-sm">
          {MENU.map((m) => (
            <button
              key={m.key}
              onClick={() => setSection(m.key)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition ${
                section === m.key
                  ? 'bg-primary-subtle text-primary'
                  : 'text-font-secondary hover:bg-bg'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="flex-1">
          {section === 'paises' && <CountriesSection />}
          {section === 'catalogs' && <CatalogsSection />}
          {section === 'roles' && <RolesSection />}
          {section === 'politicas' && <PoliciesSection />}
        </div>
      </div>
    </div>
  );
}

function CountriesSection() {
  const { data } = useCountries();
  const year = new Date().getFullYear();
  const { data: holidays } = useHolidays(undefined, year);
  const [editing, setEditing] = useState<any | null>(null);

  return (
    <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-font-primary">
            Países y Feriados
          </h2>
          <p className="mt-1 text-xs text-font-secondary">
            Administra los países donde ISOFT tiene operaciones y configura los
            feriados para cada uno (año {year}).
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {data?.items?.map((c: any) => {
          const hcount =
            holidays?.items?.filter((h: any) => h.countryId === c.id).length ?? 0;
          return (
            <div
              key={c.id}
              className="flex items-center gap-4 rounded-md border border-border p-4 hover:border-primary"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-subtle text-xs font-bold text-primary">
                {c.code}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-font-primary">{c.nameEs}</p>
                <p className="text-xs text-font-secondary">
                  {c.vacationDaysPerYear} días · {hcount} feriados configurados
                </p>
              </div>
              <button
                onClick={() => setEditing(c)}
                className="flex h-9 items-center gap-2 rounded-md border border-border px-3 text-xs font-semibold text-font-secondary hover:bg-bg"
              >
                <Pencil size={12} /> Editar feriados
              </button>
            </div>
          );
        })}
      </div>

      {editing && <HolidaysModal country={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function HolidaysModal({ country, onClose }: { country: any; onClose: () => void }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useHolidays(country.id, year);
  const create = useCreateHoliday();
  const del = useDeleteHoliday();
  const [form, setForm] = useState({
    date: '',
    name: '',
    type: 'OFFICIAL',
    recurring: false,
  });

  const add = async () => {
    if (!form.date || !form.name) return;
    await create.mutateAsync({
      countryId: country.id,
      date: form.date,
      name: form.name,
      type: form.type,
      recurring: form.recurring,
    });
    setForm({ date: '', name: '', type: 'OFFICIAL', recurring: false });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-font-primary">
              Feriados · {country.nameEs}
            </h3>
            <p className="text-xs text-font-secondary">
              Gestiona los días no laborables para {country.code}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-border bg-white px-2 text-sm"
            >
              {[year - 1, year, year + 1, year + 2].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              onClick={onClose}
              className="h-9 rounded-md border border-border px-3 text-sm text-font-secondary hover:bg-bg"
            >
              Cerrar
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-12 gap-2">
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="input col-span-3 h-10"
          />
          <input
            placeholder="Nombre del feriado"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input col-span-4 h-10"
          />
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="input col-span-2 h-10"
          >
            <option value="OFFICIAL">Oficial</option>
            <option value="REGIONAL">Regional</option>
            <option value="COMPANY">Empresa</option>
          </select>
          <label className="col-span-2 flex items-center gap-2 text-xs text-font-secondary">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
            />
            Recurrente
          </label>
          <button
            onClick={add}
            disabled={create.isPending}
            className="col-span-1 flex h-10 items-center justify-center rounded-md bg-primary text-white hover:bg-primary-dark disabled:opacity-60"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="py-2">Fecha</th>
                <th className="py-2">Nombre</th>
                <th className="py-2">Tipo</th>
                <th className="py-2">Recurrente</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.map((h: any) => (
                <tr key={h.id} className="border-b border-border-light">
                  <td className="py-2 font-mono text-xs text-font-primary">
                    {new Date(h.date).toLocaleDateString('es')}
                  </td>
                  <td className="py-2 text-font-primary">{h.name}</td>
                  <td className="py-2 text-font-secondary">{h.type}</td>
                  <td className="py-2 text-font-secondary">
                    {h.recurring ? 'Sí' : 'No'}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => {
                        if (confirm('¿Eliminar feriado?')) del.mutate(h.id);
                      }}
                      className="text-error hover:text-error"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.items?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-font-tertiary">
                    Sin feriados registrados para {year}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RolesSection() {
  const { data } = useUsers();
  const update = useUpdateUserRole();
  return (
    <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
      <div className="mb-5">
        <h2 className="text-base font-semibold text-font-primary">Roles y Permisos</h2>
        <p className="mt-1 text-xs text-font-secondary">
          Asigna el rol del sistema a cada usuario. ADMIN tiene control total, HR gestiona
          colaboradores, MANAGER aprueba permisos, EMPLOYEE solo accede a su perfil.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
            <th className="py-3">Usuario</th>
            <th className="py-3">Rol actual</th>
            <th className="py-3">Cambiar rol</th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.map((u: any) => (
            <tr key={u.id} className="border-b border-border-light">
              <td className="py-3 text-font-primary">
                <div className="font-semibold">{u.email}</div>
                <div className="text-xs text-font-tertiary">
                  {u.employee
                    ? `${u.employee.firstName} ${u.employee.lastName}`
                    : 'Sin colaborador'}
                </div>
              </td>
              <td className="py-3">
                <span className="rounded-full bg-primary-subtle px-2 py-0.5 text-[11px] font-semibold text-primary">
                  {u.role}
                </span>
              </td>
              <td className="py-3">
                <select
                  value={u.role}
                  onChange={(e) => update.mutate({ id: u.id, role: e.target.value })}
                  className="h-9 rounded-md border border-border bg-white px-2 text-sm"
                >
                  <option value="ADMIN">ADMIN</option>
                  <option value="HR">HR</option>
                  <option value="MANAGER">MANAGER</option>
                  <option value="EMPLOYEE">EMPLOYEE</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DEFAULT_POLICIES = [
  {
    key: 'vacation.default',
    name: 'Días de vacaciones anuales (default)',
    value: { days: 15 },
  },
  {
    key: 'vacation.carryOver',
    name: 'Días acumulables al año siguiente',
    value: { maxDays: 5 },
  },
  {
    key: 'leave.maxConsecutiveVacation',
    name: 'Máx. días consecutivos de vacaciones sugeridos',
    value: { days: 15 },
  },
  {
    key: 'leave.approvalTimeoutDays',
    name: 'Escalar permiso si no se aprueba en X días',
    value: { days: 3 },
  },
];

function PoliciesSection() {
  const { data } = usePolicies();
  const upsert = useUpsertPolicy();
  const del = useDeletePolicy();
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({
    key: '',
    name: '',
    description: '',
    value: '',
  });

  const openEdit = (p: any) => {
    setEditing(p);
    setForm({
      key: p.key,
      name: p.name,
      description: p.description ?? '',
      value: JSON.stringify(p.value, null, 2),
    });
  };

  const openNew = () => {
    setEditing({});
    setForm({ key: '', name: '', description: '', value: '{\n  \n}' });
  };

  const save = async () => {
    let parsedValue: any;
    try {
      parsedValue = JSON.parse(form.value);
    } catch {
      alert('El valor debe ser JSON válido');
      return;
    }
    await upsert.mutateAsync({
      key: form.key,
      name: form.name,
      description: form.description || undefined,
      value: parsedValue,
      active: true,
    });
    setEditing(null);
  };

  const ensureDefaults = async () => {
    for (const p of DEFAULT_POLICIES) {
      await upsert.mutateAsync({ ...p, active: true });
    }
  };

  return (
    <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-font-primary">
            Políticas de Vacaciones y Permisos
          </h2>
          <p className="mt-1 text-xs text-font-secondary">
            Parámetros globales del sistema. Los valores están en formato JSON.
          </p>
        </div>
        <div className="flex gap-2">
          {(data?.items?.length ?? 0) === 0 && (
            <button
              onClick={ensureDefaults}
              className="flex h-10 items-center gap-2 rounded-md border border-border px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
            >
              Crear defaults
            </button>
          )}
          <button
            onClick={openNew}
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
          >
            <Plus size={14} /> Nueva política
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {data?.items?.map((p: any) => (
          <div
            key={p.id}
            className="rounded-md border border-border p-4 hover:border-primary"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-font-primary">{p.name}</p>
                <p className="text-xs text-font-tertiary">{p.key}</p>
                {p.description && (
                  <p className="mt-1 text-xs text-font-secondary">{p.description}</p>
                )}
                <pre className="mt-2 overflow-x-auto rounded bg-bg p-2 text-xs text-font-primary">
                  {JSON.stringify(p.value, null, 2)}
                </pre>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openEdit(p)}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary hover:bg-bg"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => {
                    if (confirm('¿Eliminar política?')) del.mutate(p.key);
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-error hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold">
              {editing.id ? 'Editar política' : 'Nueva política'}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-font-secondary">Clave (key)</label>
                <input
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  disabled={!!editing.id}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Descripción</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Valor (JSON)</label>
                <textarea
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="input min-h-[120px] w-full font-mono text-xs"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="h-10 rounded-md border border-border px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                className="h-10 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogsSection() {
  const [cat, setCat] = useState<'dept' | 'pos'>('dept');
  const { data: depts } = useDepartments();
  const { data: positions } = usePositions();
  const createDept = useCreateDepartment();
  const delDept = useDeleteDepartment();
  const createPos = useCreatePosition();
  const delPos = useDeletePosition();
  const [newName, setNewName] = useState('');
  const [newLevel, setNewLevel] = useState(1);

  const handleAdd = async () => {
    if (!newName) return;
    if (cat === 'dept') {
      await createDept.mutateAsync({ name: newName });
    } else {
      await createPos.mutateAsync({ title: newName, level: newLevel });
    }
    setNewName('');
    setNewLevel(1);
  };

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="flex border-b border-border">
        {(['dept', 'pos'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setCat(k)}
            className={`relative px-6 py-4 text-sm font-semibold ${
              cat === k ? 'text-primary' : 'text-font-secondary'
            }`}
          >
            {k === 'dept' ? 'Departamentos' : 'Puestos'}
            {cat === k && (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
            )}
          </button>
        ))}
      </div>

      <div className="p-6">
        <div className="mb-4 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-font-secondary">
              {cat === 'dept' ? 'Nuevo departamento' : 'Nuevo puesto'}
            </label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={cat === 'dept' ? 'Ej. Recursos Humanos' : 'Ej. Analista Jr'}
              className="input h-10 w-full"
            />
          </div>
          {cat === 'pos' && (
            <div>
              <label className="text-xs text-font-secondary">Nivel</label>
              <input
                type="number"
                min={1}
                max={20}
                value={newLevel}
                onChange={(e) => setNewLevel(Number(e.target.value))}
                className="input h-10 w-24"
              />
            </div>
          )}
          <button
            onClick={handleAdd}
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
          >
            <Plus size={14} /> Agregar
          </button>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
              <th className="py-3">Nombre</th>
              <th className="py-3">{cat === 'dept' ? 'Padre' : 'Nivel'}</th>
              <th className="py-3">Detalle</th>
              <th className="py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(cat === 'dept' ? depts?.items : positions?.items)?.map((r: any) => (
              <tr key={r.id} className="border-b border-border-light">
                <td className="py-3 font-semibold text-font-primary">
                  {r.name ?? r.title}
                </td>
                <td className="py-3 text-font-secondary">
                  {cat === 'dept' ? r.parent?.name ?? '–' : `Nivel ${r.level}`}
                </td>
                <td className="py-3 text-font-secondary">
                  {cat === 'dept'
                    ? `${r._count?.employees ?? 0} colaboradores`
                    : r.description ?? '–'}
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => {
                      if (confirm('¿Desactivar este registro?')) {
                        if (cat === 'dept') delDept.mutate(r.id);
                        else delPos.mutate(r.id);
                      }
                    }}
                    className="text-error hover:text-error"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
